import Phaser from "phaser";
import { detectPlatform } from "@/platform/detectPlatform";
import { GameConfig } from "@/config/GameConfig";
import { createButton } from "@/ui/Button";
import { createNativeTextInput, type NativeTextInputHandle } from "@/ui/NativeTextInput";
import { createNameEntryModal, type NameEntryModalHandle } from "@/ui/NameEntryModal";
import { createShareChallengeModal, type ShareChallengeModalHandle } from "@/ui/ShareChallengeModal";
import { hexToCss } from "@/ui/colors";
import { resolveTitleSize, LB_BACK_BUTTON_X, LB_BACK_BUTTON_WIDTH, LB_BACK_BUTTON_HEIGHT } from "@/ui/leaderboardLayout";
import { DailyChallengeClient } from "@/daily/DailyChallengeClient";
import { DailyLocalState } from "@/daily/DailyLocalState";
import { ScoreChallengeClient } from "@/daily/ScoreChallengeClient";
import { resolveChallengeBaseUrl, buildChallengeUrl, isSharingEnabled } from "@/daily/shareUrl";
import { canStartAttempt, resolveAttemptOutcome } from "@/daily/attemptReservation";
import { hasVerifiedRankedBest, resolveAttemptStatusOutcome } from "@/daily/attemptStatus";
import { buildQaPreviewContext, canStartQaPreview, shouldEnterQaPreview } from "@/daily/qaPreview";
import { DAILY_INCOMPLETE_LABEL } from "@/daily/statusLabels";
import { formatScoringRuleText } from "@/daily/dailyPoints";
import { describePressureRamp, pressureRampIdFromProfile } from "@/daily/pressure";
import { DAILY_RECIPES } from "@/daily/recipes";
import { describeV6Pressure } from "@/daily/v6/profiles";
import {
  addDaysToChallengeId,
  getDailyQaDate,
  getSimulatedAttemptsRemaining,
  isDailyQaAllowedHost,
  resetDailyQaState,
  setDailyQaDate,
  setSimulatedAttemptsRemaining,
} from "@/daily/dailyQa";
import { trackDailyChallengeOpened, trackScoreChallengeRunStarted, trackScoreSharePromptViewed } from "@/analytics/gameAnalytics";
import { attemptsRemainingBucket } from "@/daily/scoreChallenge";
import { SCORE_CHALLENGE_QA_PRESETS, type ScoreChallengeQaPresetId } from "@/daily/scoreChallengeQa";
import type { DailyChallengeSpecAny, DailyBiteMode, DailyMapFamily } from "@/daily/types";
import type { DailyGameSceneContext } from "@/daily/DailyRunContext";
import type { SourceChallengeContext } from "@/daily/scoreChallenge";
import type { ShareContext } from "@/analytics/events";
import { attachOrientationGate, type OrientationGateHandle } from "@/scenes/OrientationGate";

/**
 * "loading" covers fetching today's challenge spec; "loading-status"
 * covers fetching the *authoritative* attempts-remaining status for that
 * challenge - kept as a distinct state (rather than folding it into
 * "loading") specifically so the screen can show "Loading attempts…" and
 * so `startAttempt()`'s guard can require having passed through it
 * successfully before ever enabling START ATTEMPT.
 *
 * "qa-preview" is a completely separate branch, reached instead of
 * "loading-status"/"ready"/"no-attempts" whenever a QA date override is
 * active (see `refresh()`) - it never fetches or displays a real
 * attempts-remaining count at all, real or fabricated, and its own
 * PLAY QA PREVIEW action never reserves a real attempt. This is what
 * fixes the exact bug where a QA-previewed date briefly showed a real
 * (or QA-challenge-scoped) attempts count and then STARTING… silently
 * fell through to reserving *today's* real, possibly-exhausted attempt -
 * a QA preview has no attempts concept to get out of sync in the first
 * place.
 */
type LoadState = "loading" | "loading-status" | "ready" | "no-attempts" | "qa-preview" | "offline" | "error";

const BITE_MODE_LABEL: Record<DailyBiteMode, string> = {
  standard: "STANDARD — CHOOSE YOUR BITE",
  safe_only: "SAFE ONLY",
  rush_only: "RUSH ONLY",
  chaos_only: "CHAOS ONLY",
};

/** V1-only display label - kept so an already-stored `formatVersion: 1` date (see the README's "V1 stored rows" section) still shows something meaningful instead of crashing. Never used for a fresh V2 date, which has its own `spec.layoutName`. */
const MAP_FAMILY_LABEL: Record<DailyMapFamily, string> = {
  open_arena: "OPEN ARENA",
  corridors: "CORRIDORS",
  islands: "ISLANDS",
  rings: "RINGS",
  chambers: "CHAMBERS",
  pinch_points: "PINCH POINTS",
};

/**
 * The Daily Challenge pre-run screen: today's challenge, its rule/
 * modifier, attempts remaining, a live countdown to the next
 * `America/New_York` reset, and START ATTEMPT. Reservation happens here
 * (`DailyChallengeClient.reserveAttempt()`) - by the time `GameScene`
 * starts, the attempt is already consumed server-side; see the PR
 * description's attempt-consumption rule.
 */
export class DailyChallengeScene extends Phaser.Scene {
  private loadState: LoadState = "loading";
  private errorMessage = "";
  private spec: DailyChallengeSpecAny | null = null;
  private challengeId = "";
  private serverTimeMs = 0;
  private nextResetAtMs = 0;
  /** `performance.now()` at the moment `serverTimeMs` was captured - the countdown advances from this anchor, never from the player's own wall clock. */
  private anchorPerfMs = 0;
  private attemptsRemaining = 0;
  /** True only after a `getMyStatus()` call for the *current* `challengeId` has resolved successfully - `startAttempt()`'s guard requires this, so a reservation can never fire before the authoritative attempt count is known. Reset on every `refresh()`, so a new day's `challengeId` always re-earns it from a fresh server response rather than inheriting yesterday's. */
  private hasLoadedStatus = false;
  private bestScore: number | null = null;
  private bestCompleted = false;
  private hasResult = false;
  /**
   * True only when `get_my_daily_rank()` reports BOTH `has_result` (a
   * ranked, `best_completed`, `best_submitted_at`-not-null row - see that
   * function's SQL) AND a non-null `daily_points` (never true for a V1/
   * legacy date, which has no scoring profile to have computed one from).
   * This is the exact same "genuine, comparison-worthy Daily V2 best"
   * check `ScoreChallengeScene.loadChallenge()` already uses - the single
   * source of truth for whether CHALLENGE A FRIEND should ever be shown
   * here, independent of `bestScore`/`bestCompleted` above (which mirror
   * `get_my_daily_status()`'s own `daily_participation_bests` row for
   * THIS client's current scoring-rules version - see
   * `DAILY_SCORING_RULES_VERSION`'s doc - and can't by themselves
   * distinguish "V1 best" from "V2 best" the way `dailyPoints !== null`
   * here can).
   * Refreshed on every `refresh()`, so it always reflects the CURRENT
   * server-verified best - never the most recent attempt, and never
   * stale across a rollover to a new `challengeId`.
   */
  private hasRankedBest = false;
  private isReserving = false;
  private isCreatingChallenge = false;
  private shareCreateError = "";
  /** True once this scene has either shown the "save your name" prompt or confirmed one already exists - never asks twice in the same scene lifetime, even if the player dismisses it and taps CHALLENGE A FRIEND again (that retry just shares as ANONYMOUS, same fallback the server already applies). */
  private hasCheckedNameForShare = false;
  private shareContext: ShareContext = "own_result";
  private isQaOverrideActive = false;
  /** Set once by `teardown()` - guards every post-`await` continuation (`refresh()`, `startAttempt()`) so a stale response arriving after shutdown/scene-swap can never touch a destroyed scene's Game Objects. */
  private isDisposed = false;

  private mainContainer!: Phaser.GameObjects.Container;
  /**
   * Rebuilt fresh in every `buildLayout()` call (never reused across
   * rebuilds) - `buildLayout()` starts with `mainContainer.removeAll(true)`,
   * which destroys whatever was in the container a moment ago. An earlier
   * version kept a single long-lived `countdownText` instance that was
   * added into `mainContainer` once `loadState` reached "ready", then left
   * for a *later* rebuild's `removeAll(true)` to destroy while still being
   * referenced here and re-added a few lines later - Phaser's Container
   * throws synchronously when a destroyed child is re-added
   * (`Cannot read properties of undefined (reading 'sys')`), and any
   * survivor of that still crashes `update()`'s next `setText()` call
   * (`Cannot read properties of null (reading 'drawImage')`) since its
   * internal canvas texture is gone. That first throw happened
   * synchronously inside `startAttempt()`'s own `this.buildLayout()` call -
   * *before* `reserveAttempt()` was ever reached - which is exactly why no
   * `daily-attempt` request went out and the button stayed on "STARTING…"
   * forever (the throw aborted the async function before `isReserving`
   * could ever be reset). Always create a new Text object here instead.
   */
  private countdownText?: Phaser.GameObjects.Text;
  private qaDateInput?: NativeTextInputHandle;
  private qaPanelOpen = false;
  private nameModal!: NameEntryModalHandle;
  private shareModal!: ShareChallengeModalHandle;
  /** Present only when reached via a "Beat My Score" challenge link's BEAT THIS SCORE flow (`ScoreChallengeScene`) - threaded into `GameScene`'s `DailyGameSceneContext` unchanged, never re-fetched here. `null` for every normal Daily Challenge entry (MENU -> DAILY CHALLENGE). */
  private sourceChallenge: SourceChallengeContext | null = null;
  /** PR31 follow-up fix: see `OrientationGate.ts`'s own doc. No `requiresExplicitResume` - this Scene has no run/state to protect, so rotating back to portrait restores interaction immediately. */
  private orientationGate!: OrientationGateHandle;

  constructor() {
    super("DailyChallengeScene");
  }

  create(data: { sourceChallenge?: SourceChallengeContext } = {}): void {
    this.loadState = "loading";
    this.spec = null;
    this.attemptsRemaining = 0;
    this.hasLoadedStatus = false;
    this.hasRankedBest = false;
    this.isReserving = false;
    this.isCreatingChallenge = false;
    this.shareCreateError = "";
    this.hasCheckedNameForShare = false;
    this.qaPanelOpen = false;
    this.isDisposed = false;
    this.sourceChallenge = data.sourceChallenge ?? null;
    // Present only when reached via a friend's challenge link's BEAT THIS
    // SCORE flow - sharing from THIS screen afterward is still a "reshare"
    // in the same sense `DailyResultScene` already tracks it, even though
    // no result was submitted during *this* particular scene visit.
    this.shareContext = this.sourceChallenge ? "reshare" : "own_result";
    this.cameras.main.setBackgroundColor(GameConfig.colors.background);
    this.mainContainer = this.add.container(0, 0);
    this.countdownText = undefined;
    this.nameModal = createNameEntryModal(this, 90, {
      onSave: (name) => this.handleSaveName(name),
      onNotNow: () => {},
    });
    this.shareModal = createShareChallengeModal(this, 95, this.shareContext);

    this.buildLayout();
    this.scale.on("resize", this.onResize, this);
    this.events.once("shutdown", () => this.teardown());
    this.events.once("destroy", () => this.teardown());
    this.orientationGate = attachOrientationGate(this);
    this.orientationGate.check();

    void this.refresh();
  }

  private teardown(): void {
    this.isDisposed = true;
    this.scale.off("resize", this.onResize, this);
    this.qaDateInput?.destroy();
    this.nameModal.destroy();
    this.shareModal.destroy();
  }

  private onResize(): void {
    if (this.isDisposed) return;
    this.buildLayout();
    this.nameModal.relayout();
    this.shareModal.relayout();
  }

  update(): void {
    if (this.loadState !== "ready" && this.loadState !== "no-attempts") return;
    // `.active` is Phaser's own destroyed-flag - a defensive guard against
    // reading a torn-down Text object, on top of `countdownText` no longer
    // being reused across rebuilds (see the field's doc comment above).
    if (!this.countdownText || !this.countdownText.active) return;
    const remainingMs = Math.max(0, this.nextResetAtMs - (this.serverTimeMs + (performance.now() - this.anchorPerfMs)));
    this.countdownText.setText(`Next challenge in ${formatCountdown(remainingMs)}`);
  }

  // ---------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------

  private async refresh(): Promise<void> {
    this.loadState = "loading";
    // Never carry a stale/fabricated count across a refresh (a brand new
    // page load, a RETRY, or a rollover into a new challenge_id) - there's
    // no attempts-remaining number on screen at all until a fresh,
    // successful status load for the current challenge says what it is.
    this.attemptsRemaining = 0;
    this.hasLoadedStatus = false;
    this.hasRankedBest = false;
    this.buildLayout();

    const hostname = window.location.hostname;
    const qaDate = getDailyQaDate(hostname);
    const result = await DailyChallengeClient.fetchTodayChallenge(qaDate);
    if (this.isDisposed) return;
    if (result.state !== "ok" || !result.challenge || !result.challengeId) {
      this.loadState = result.state === "offline" ? "offline" : "error";
      this.errorMessage = result.message ?? "";
      this.buildLayout();
      return;
    }

    this.spec = result.challenge;
    this.challengeId = result.challengeId;
    this.serverTimeMs = result.serverTimeMs ?? Date.now();
    this.nextResetAtMs = result.nextResetAtMs ?? this.serverTimeMs;
    this.anchorPerfMs = performance.now();
    this.isQaOverrideActive = result.isQaOverride ?? false;

    // A QA date override never has a real attempts-remaining concept at
    // all - not today's real count, and not even the QA-challenge's own
    // reservation count - so there is nothing to fetch here. Re-checking
    // the host client-side too (never just trusting the server's
    // `isQaOverride` flag alone) is what keeps this branch unreachable on
    // production even through crafted local state - see `startQaPreview()`.
    if (shouldEnterQaPreview(this.isQaOverrideActive, hostname)) {
      this.loadState = "qa-preview";
      this.buildLayout();
      return;
    }

    this.loadState = "loading-status";
    this.buildLayout();

    // `getMyRank()` runs alongside `getMyStatus()` purely to answer "does a
    // genuine, server-verified Daily V2 best exist right now" for
    // CHALLENGE A FRIEND's own visibility (`hasRankedBest` below) - it is
    // NOT the authoritative attempts-remaining gate (that stays
    // `getMyStatus()`/`resolveAttemptStatusOutcome()` alone, unchanged), and
    // a rank-fetch failure never blocks START ATTEMPT or falls back to a
    // guessed attempts count - it only means CHALLENGE A FRIEND stays
    // hidden until the next successful refresh.
    // PR34: attempts are shared; "your best"/rank are this device's
    // Mobile/Desktop board - the same board a run started from here lands on.
    const platform = detectPlatform();
    const [statusResult, rankResult] = await Promise.all([
      DailyChallengeClient.getMyStatus(this.challengeId, platform),
      DailyChallengeClient.getMyRank(this.challengeId, platform),
    ]);
    if (this.isDisposed) return;

    const simulated = getSimulatedAttemptsRemaining(hostname);
    const outcome = resolveAttemptStatusOutcome(statusResult, simulated);

    if (outcome.type === "failed") {
      // Authoritative status failed to load - stay recoverable and never
      // assume 3 attempts. RETRY re-runs this whole method from scratch.
      this.loadState = outcome.loadState;
      this.errorMessage = "Could not load your attempt status.";
      this.buildLayout();
      return;
    }

    this.hasLoadedStatus = true;
    this.attemptsRemaining = outcome.attemptsRemaining;
    this.bestScore = outcome.bestScore;
    this.bestCompleted = outcome.bestCompleted;
    this.hasResult = outcome.hasResult;
    this.hasRankedBest = hasVerifiedRankedBest(rankResult);

    DailyLocalState.save({
      challengeId: this.challengeId,
      attemptsRemaining: this.attemptsRemaining,
      bestScore: this.bestScore,
      bestCompleted: this.bestCompleted,
    });

    this.loadState = outcome.type;
    this.buildLayout();

    const openedSpec = this.spec;
    trackDailyChallengeOpened({
      challengeId: this.challengeId,
      generatorVersion: openedSpec.generatorVersion,
      biteMode: openedSpec.biteMode,
      layoutId: openedSpec.formatVersion === 1 ? openedSpec.mapFamily : openedSpec.formatVersion === 2 ? openedSpec.layoutArchetype : openedSpec.archetype,
      scoringProfileId: openedSpec.formatVersion === 1 ? undefined : openedSpec.scoringProfile.id,
      attemptsRemaining: this.attemptsRemaining,
    });
  }

  private async startAttempt(): Promise<void> {
    // `canStartAttempt` is the single gate for the whole method - once
    // `isReserving` flips true below, any re-entrant click (or a stale
    // callback from a button destroyed mid-tween, see `Button.ts`) hits
    // this same check and is a no-op. That guarantees at most one
    // `daily-attempt` request per attempt, regardless of how many times
    // the button gets clicked while one is already in flight. It also
    // requires `hasLoadedStatus` and a positive `attemptsRemaining` - a
    // reservation can never fire before the authoritative status has
    // loaded, or once it's already loaded and says zero (the server's 409
    // for that case is defense-in-depth, not the primary guard).
    if (!canStartAttempt(this.isReserving, this.spec !== null, this.hasLoadedStatus, this.attemptsRemaining)) return;
    this.isReserving = true;
    this.buildLayout();

    const result = await DailyChallengeClient.reserveAttempt();
    // The scene may have been shut down (BACK, or navigating away) while
    // this request was in flight - a stale response must never touch a
    // torn-down scene's Game Objects or start a second scene transition.
    if (this.isDisposed) return;
    this.isReserving = false;

    const outcome = resolveAttemptOutcome(result);
    switch (outcome.type) {
      case "exhausted":
        this.attemptsRemaining = 0;
        this.loadState = "no-attempts";
        this.buildLayout();
        return;
      case "failed":
        // Reservation failed - no attempt was consumed. Stay on this
        // screen with a friendly retry, never silently proceed into a run.
        this.loadState = outcome.loadState;
        this.errorMessage = outcome.message;
        this.buildLayout();
        return;
      case "start-game": {
        const context: DailyGameSceneContext = {
          spec: this.spec!,
          attemptNumber: outcome.attemptNumber,
          attemptsRemaining: outcome.attemptsRemaining,
          ...(this.sourceChallenge ? { sourceChallenge: this.sourceChallenge } : {}),
        };
        if (this.sourceChallenge) {
          trackScoreChallengeRunStarted(attemptsRemainingBucket(outcome.attemptsRemaining), this.spec!.generatorVersion);
        }
        this.scene.start("GameScene", { daily: context });
        return;
      }
    }
  }

  /**
   * PLAY QA PREVIEW - starts the currently-loaded QA date's spec
   * directly, with no reservation step at all: never calls
   * `DailyChallengeClient.reserveAttempt()` (`daily-attempt`), so there is
   * no real attempt to consume, no `daily_participation` row touched, and
   * no way for today's real (possibly exhausted) attempt count to ever
   * enter this path - the exact bug this replaces (STARTING… silently
   * reserving *today's* real attempt for a QA-previewed date) is
   * structurally impossible here, not just guarded against.
   *
   * Triple-checked before starting (spec loaded, server already confirmed
   * the override via `isQaOverrideActive`, and the host re-checked here
   * again) purely as defense in depth - this button only ever exists in
   * the DOM/display-list at all when `buildLayout()` already chose
   * `"qa-preview"`, which itself required both of the last two.
   */
  private startQaPreview(): void {
    if (!this.spec || !canStartQaPreview(true, this.isQaOverrideActive, window.location.hostname)) return;
    this.scene.start("GameScene", { daily: buildQaPreviewContext(this.spec) });
  }

  /** The single gate for rendering CHALLENGE A FRIEND here - see `hasRankedBest`'s doc for exactly what it does and doesn't require. `!!this.spec && this.spec.formatVersion !== 1` (V2-V5 or V6, PR25) is redundant with `hasRankedBest` in practice (a V1 date's `daily_points` is always null) but kept as explicit defense in depth. */
  private canShareChallenge(): boolean {
    return !!this.spec && this.spec.formatVersion !== 1 && this.hasRankedBest && isSharingEnabled();
  }

  /**
   * CHALLENGE A FRIEND from the Daily summary/status screen - reuses the
   * exact same `ScoreChallengeClient.createOrGetChallenge()` call,
   * `ShareChallengeModal`, and disclosure/SHARE/COPY LINK behavior
   * `DailyResultScene` already uses, so a player who closed the result
   * screen can still share their current best without playing again.
   *
   * Never calls `reserveAttempt()`/`daily-attempt` - creating or reopening
   * the share modal cannot reserve, consume, restore, or change an
   * attempt, independent of `attemptsRemaining`/`isReserving` above. The
   * actual target shared is always whatever `create-score-challenge`
   * independently re-derives from `daily_participation_bests` (THIS
   * client's current scoring-rules version) at click time - never a value
   * cached from this scene's own `refresh()` - so it's always the
   * CURRENT server-verified best, never merely the most recent attempt
   * and never a different version's number.
   */
  private async challengeAFriend(): Promise<void> {
    if (this.isCreatingChallenge || !this.canShareChallenge()) return;

    // "If a public display name is still required, reuse the existing
    // safe name-save flow" - the same `NameEntryModal`/`setDisplayName()`
    // path `DailyResultScene` uses after a fresh submission. Checked once
    // per scene lifetime (`hasCheckedNameForShare`): a dismiss (NOT NOW)
    // never re-prompts on a later click - it just proceeds and shares as
    // ANONYMOUS, the exact same fallback the server already applies.
    if (!this.hasCheckedNameForShare) {
      this.hasCheckedNameForShare = true;
      const nameResult = await DailyChallengeClient.getMyDisplayName();
      if (this.isDisposed) return;
      if (nameResult.state === "ok" && nameResult.data === null) {
        this.nameModal.open({ headline: "SAVE YOUR NAME", contextLine: "Your name is shown to whoever opens your challenge link." });
        return;
      }
    }

    this.isCreatingChallenge = true;
    this.shareCreateError = "";
    this.buildLayout();

    // PR34: the link's frozen target is this platform's best.
    const result = await ScoreChallengeClient.createOrGetChallenge(detectPlatform());
    this.isCreatingChallenge = false;
    if (this.isDisposed) return;

    if (result.state !== "ok" || !result.publicId || result.targetDailyPoints === undefined) {
      this.shareCreateError = "Could not create your challenge link. Try again.";
      this.buildLayout();
      return;
    }
    this.buildLayout();

    const baseUrl = resolveChallengeBaseUrl(window.location.hostname, window.location.origin);
    const url = buildChallengeUrl(baseUrl, result.publicId);
    trackScoreSharePromptViewed(this.shareContext);
    this.shareModal.open(result.creatorDisplayName ?? "ANONYMOUS", result.targetDailyPoints, url);
  }

  private async handleSaveName(name: string): Promise<{ ok: boolean; error?: string }> {
    const result = await DailyChallengeClient.setDisplayName(name);
    if (result.state !== "ok") {
      return { ok: false, error: result.state === "error" ? result.message : "Could not save name right now." };
    }
    // The player was mid-CHALLENGE-A-FRIEND when the name prompt
    // interrupted them - finish what they started instead of making them
    // tap the button a second time. `hasCheckedNameForShare` is already
    // true, so this re-entry skips straight to creating/opening the
    // share modal.
    void this.challengeAFriend();
    return { ok: true };
  }

  // ---------------------------------------------------------------------
  // Layout
  // ---------------------------------------------------------------------

  private buildLayout(): void {
    this.mainContainer.removeAll(true);
    this.qaDateInput?.setVisible(false);
    const { width, height } = this.scale;
    const centerX = width / 2;

    const titleSize = Math.max(28, Math.min(42, width * 0.07));
    const title = this.add
      .text(centerX, height * 0.08, "DAILY CHALLENGE", {
        fontFamily: "Arial, sans-serif",
        fontSize: `${titleSize}px`,
        fontStyle: "bold",
        color: "#f5f7fa",
      })
      .setOrigin(0.5);
    // Mobile beta fix: on a narrow phone "DAILY CHALLENGE" at its desired
    // size reached into the BACK button's own zone - shrink it (using its
    // real measured width, the same safety check Campaign's
    // LeaderboardScene already uses) only if it actually would overlap.
    // No-op at every width wide enough that this wasn't a risk (including
    // all of desktop).
    const resolvedTitleSize = resolveTitleSize(titleSize, title.width, width);
    if (resolvedTitleSize !== titleSize) title.setFontSize(resolvedTitleSize);
    this.mainContainer.add(title);

    // Mobile beta fix: BACK used to sit at a DIFFERENT y (height * 0.06)
    // than the title (height * 0.08) - the header's two controls
    // effectively occupied two overlapping rows instead of one shared row,
    // which is what every other header in this app (WorldSelectScene,
    // LevelSelectScene, the Leaderboard scenes) already does. Aligning
    // BACK to the title's own y reserves it a real, non-overlapping slot
    // to the title's left without moving/resizing the content below,
    // which flows from `title.y` and is unaffected by BACK's y.
    const backButton = createButton(this, LB_BACK_BUTTON_X, title.y, "BACK", () => this.scene.start("MenuScene"), {
      width: LB_BACK_BUTTON_WIDTH,
      height: LB_BACK_BUTTON_HEIGHT,
      fontSize: "15px",
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
    });
    this.mainContainer.add(backButton);

    let y = title.y + resolvedTitleSize;

    switch (this.loadState) {
      case "loading":
        y = this.renderMessage(centerX, y, "LOADING TODAY'S CHALLENGE…");
        break;
      case "loading-status":
        // The challenge itself has loaded - this is specifically the
        // authoritative attempts-remaining fetch, called out separately
        // so the player never sees a blank/guessed attempts count while
        // it's still in flight.
        y = this.renderMessage(centerX, y, "LOADING ATTEMPTS…");
        break;
      case "offline":
        y = this.renderMessage(centerX, y, "COULDN'T REACH THE SERVER. DAILY CHALLENGE NEEDS A CONNECTION.");
        y = this.renderActionButtons(centerX, y);
        break;
      case "error":
        y = this.renderMessage(centerX, y, this.errorMessage || "Something went wrong.");
        y = this.renderActionButtons(centerX, y);
        break;
      case "ready":
      case "no-attempts":
        y = this.renderChallengeInfo(centerX, y);
        break;
      case "qa-preview":
        y = this.renderQaPreviewInfo(centerX, y);
        break;
    }

    // Created fresh here every time rather than reused - see the
    // `countdownText` field's doc comment for why reusing a single
    // long-lived instance across rebuilds is exactly what corrupted the
    // scene.
    this.countdownText = undefined;
    if (this.loadState === "ready" || this.loadState === "no-attempts") {
      this.countdownText = this.add
        .text(centerX, height - 30, "", {
          fontFamily: "Arial, sans-serif",
          fontSize: "14px",
          color: "#8b93a7",
        })
        .setOrigin(0.5);
      this.mainContainer.add(this.countdownText);
    }

    if (isDailyQaAllowedHost(window.location.hostname)) {
      this.buildQaPanel(centerX, height - 70);
    }
  }

  private renderMessage(centerX: number, y: number, message: string): number {
    const text = this.add
      .text(centerX, y + 30, message, {
        fontFamily: "Arial, sans-serif",
        fontSize: "16px",
        color: "#8b93a7",
        align: "center",
        wordWrap: { width: Math.min(420, this.scale.width - 40) },
      })
      .setOrigin(0.5, 0);
    this.mainContainer.add(text);
    return text.y + text.height + 20;
  }

  private renderActionButtons(centerX: number, y: number): number {
    const retry = createButton(this, centerX, y + 20, "RETRY", () => void this.refresh(), { width: 200, height: 50 });
    this.mainContainer.add(retry);
    const menu = createButton(this, centerX, y + 84, "MAIN MENU", () => this.scene.start("MenuScene"), {
      width: 200,
      height: 46,
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
    });
    this.mainContainer.add(menu);
    return y + 84 + 46;
  }

  /**
   * The date/rule/modifier/survival-details lines shared by the
   * real-attempts panel and the QA-preview panel - factored out so the
   * two only ever differ in what comes after (real attempts-remaining UI
   * vs. the QA-only unlimited-preview UI), never in how the loaded spec
   * itself is shown. Branches on `spec.formatVersion`: a V2 date shows
   * its layout/recipe name, starting length, pressure description, and
   * exact Daily Points scoring formula (Part L); a V1 date (an
   * already-stored row from before this redesign - see the README's "V1
   * stored rows" section) shows only what it always showed, since it has
   * none of that V2-only data to show.
   */
  private renderSpecHeader(centerX: number, y: number): number {
    const spec = this.spec!;

    const dateText = this.add
      .text(centerX, y, this.challengeId, { fontFamily: "Arial, sans-serif", fontSize: "15px", color: "#8b93a7" })
      .setOrigin(0.5);
    this.mainContainer.add(dateText);
    y += 30;

    const layoutLabel = spec.formatVersion === 1 ? MAP_FAMILY_LABEL[spec.mapFamily] : spec.layoutName;
    const ruleText = this.add
      .text(centerX, y, `${layoutLabel} · ${BITE_MODE_LABEL[spec.biteMode]}`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "18px",
        fontStyle: "bold",
        color: spec.biteMode === "standard" ? "#f5f7fa" : hexToCss(GameConfig.colors.star),
        align: "center",
        wordWrap: { width: Math.min(420, this.scale.width - 40) },
      })
      .setOrigin(0.5);
    this.mainContainer.add(ruleText);
    y += ruleText.height + 12;

    if (spec.formatVersion === 2) {
      const recipeText = this.add
        .text(centerX, y, DAILY_RECIPES[spec.recipeId].label, { fontFamily: "Arial, sans-serif", fontSize: "12px", color: "#8b93a7" })
        .setOrigin(0.5);
      this.mainContainer.add(recipeText);
      y += 20;
    } else if (spec.formatVersion === 3) {
      // Daily V6 (PR25) has no "recipe" concept - shows its tier instead,
      // the closest equivalent at-a-glance difficulty label.
      const tierText = this.add
        .text(centerX, y, `${spec.tier.toUpperCase()} TIER`, { fontFamily: "Arial, sans-serif", fontSize: "12px", color: "#8b93a7" })
        .setOrigin(0.5);
      this.mainContainer.add(tierText);
      y += 20;
    }

    if (spec.modifiers.length > 0) {
      const modifierText = this.add
        .text(centerX, y, spec.modifiers.map((m) => MODIFIER_LABEL[m]).join(" · "), {
          fontFamily: "Arial, sans-serif",
          fontSize: "13px",
          color: "#5b8cff",
        })
        .setOrigin(0.5);
      this.mainContainer.add(modifierText);
      y += 22;
    }

    if (spec.formatVersion !== 1) {
      y += 6;
      const survivalTag = this.add
        .text(centerX, y, "SURVIVAL — PLAY UNTIL YOU DIE", {
          fontFamily: "Arial, sans-serif",
          fontSize: "13px",
          fontStyle: "bold",
          color: "#ff5f6d",
        })
        .setOrigin(0.5);
      this.mainContainer.add(survivalTag);
      y += 24;

      const startText = this.add
        .text(centerX, y, `Starting length: ${spec.startingLength} · Initial speed: ${spec.baseStepMs}ms/step`, {
          fontFamily: "Arial, sans-serif",
          fontSize: "12px",
          color: "#8b93a7",
        })
        .setOrigin(0.5);
      this.mainContainer.add(startText);
      y += 20;

      const pressureDescription =
        spec.formatVersion === 3
          ? describeV6Pressure(spec.pressureProfileId)
          : (() => {
              const rampId = pressureRampIdFromProfile(spec.pressureProfile);
              return rampId ? describePressureRamp(rampId) : "Speed ramps up the longer you stall without growing.";
            })();
      const pressureText = this.add
        .text(centerX, y, pressureDescription, {
          fontFamily: "Arial, sans-serif",
          fontSize: "12px",
          color: "#8b93a7",
          align: "center",
          wordWrap: { width: Math.min(400, this.scale.width - 40) },
        })
        .setOrigin(0.5, 0);
      this.mainContainer.add(pressureText);
      y = pressureText.y + pressureText.height + 14;

      const scoringLabel = this.add
        .text(centerX, y, `TODAY'S SCORING (${spec.scoringProfile.label})`, {
          fontFamily: "Arial, sans-serif",
          fontSize: "12px",
          fontStyle: "bold",
          color: "#5b8cff",
        })
        .setOrigin(0.5);
      this.mainContainer.add(scoringLabel);
      y += 18;

      const scoringFormula = this.add
        .text(centerX, y, formatScoringRuleText(spec.scoringProfile), {
          fontFamily: "monospace",
          fontSize: "12px",
          color: "#f5f7fa",
        })
        .setOrigin(0.5);
      this.mainContainer.add(scoringFormula);
      y += 24;
    }

    return y;
  }

  /**
   * Only ever reached for `loadState === "ready" | "no-attempts"`, which
   * `refresh()` now only ever sets when `isQaOverrideActive` is false (a
   * QA date override always routes to `"qa-preview"`/`renderQaPreviewInfo`
   * instead) - real attempts-remaining UI and a QA preview's UI never mix
   * on screen.
   */
  private renderChallengeInfo(centerX: number, y: number): number {
    y += 20;
    y = this.renderSpecHeader(centerX, y);

    const info = this.add
      .text(centerX, y, this.spec!.formatVersion !== 1 ? "3 attempts. Best Daily Points counts." : "3 attempts. Best score counts.", {
        fontFamily: "Arial, sans-serif",
        fontSize: "14px",
        color: "#d7dbe6",
      })
      .setOrigin(0.5);
    this.mainContainer.add(info);
    y += 28;

    const attemptsText = this.add
      .text(centerX, y, `Attempts remaining: ${this.attemptsRemaining} of 3`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "15px",
        fontStyle: "bold",
        color: this.attemptsRemaining > 0 ? "#7dffb3" : "#ff5f6d",
      })
      .setOrigin(0.5);
    this.mainContainer.add(attemptsText);
    y += 30;

    if (this.hasResult) {
      const bestLabel = this.bestCompleted ? `Your best: ${this.bestScore} (COMPLETED)` : `Your best: ${this.bestScore} (${DAILY_INCOMPLETE_LABEL})`;
      const bestText = this.add
        .text(centerX, y, bestLabel, { fontFamily: "Arial, sans-serif", fontSize: "13px", color: "#8b93a7" })
        .setOrigin(0.5);
      this.mainContainer.add(bestText);
      y += 26;
    }

    if (this.sourceChallenge) {
      const targetText = this.add
        .text(centerX, y, `TARGET: ${this.sourceChallenge.targetDailyPoints.toLocaleString("en-US")} DAILY POINTS`, {
          fontFamily: "Arial, sans-serif",
          fontSize: "14px",
          fontStyle: "bold",
          color: hexToCss(GameConfig.colors.star),
        })
        .setOrigin(0.5);
      this.mainContainer.add(targetText);
      y += 26;
    }

    y += 10;
    if (this.loadState === "ready") {
      const startButton = createButton(this, centerX, y, this.isReserving ? "STARTING…" : "START ATTEMPT", () => void this.startAttempt(), {
        width: 240,
        height: 56,
        disabled: this.isReserving,
      });
      this.mainContainer.add(startButton);
      y += 72;
    }

    if (this.shareCreateError) {
      const shareError = this.add
        .text(centerX, y, this.shareCreateError, {
          fontFamily: "Arial, sans-serif",
          fontSize: "13px",
          color: "#ff5f6d",
          align: "center",
          wordWrap: { width: Math.min(420, this.scale.width - 40) },
        })
        .setOrigin(0.5, 0);
      this.mainContainer.add(shareError);
      y = shareError.y + shareError.height + 14;
    }

    // Available whenever a genuine, server-verified Daily V2 best exists
    // for today - regardless of `attemptsRemaining` (a player with zero
    // attempts left must still be able to share their existing best) and
    // regardless of whether they just played or are returning to this
    // screen later. See `canShareChallenge()`/`hasRankedBest`'s docs.
    if (this.canShareChallenge()) {
      const shareButton = createButton(this, centerX, y, this.isCreatingChallenge ? "…" : "CHALLENGE A FRIEND", () => void this.challengeAFriend(), {
        width: 240,
        height: 50,
        fillColor: 0x1c2030,
        hoverColor: 0x262c40,
        textColor: "#f5f7fa",
        disabled: this.isCreatingChallenge,
      });
      this.mainContainer.add(shareButton);
      y += 62;
    }

    const leaderboardButton = createButton(this, centerX, y, "DAILY LEADERBOARD", () => this.scene.start("DailyLeaderboardScene", { challengeId: this.challengeId }), {
      width: 240,
      height: 48,
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
    });
    this.mainContainer.add(leaderboardButton);
    y += 56;

    return y;
  }

  /**
   * `loadState === "qa-preview"` - no attempts-remaining concept exists
   * here at all (see the `LoadState` doc). PLAY QA PREVIEW is always
   * available, every time this renders: there is no counter to exhaust.
   */
  private renderQaPreviewInfo(centerX: number, y: number): number {
    y += 20;
    y = this.renderSpecHeader(centerX, y);
    y += 6;

    const banner = this.add
      .text(centerX, y, "QA PREVIEW — UNLIMITED TEST RUNS", {
        fontFamily: "Arial, sans-serif",
        fontSize: "15px",
        fontStyle: "bold",
        color: "#0f1117",
        backgroundColor: "#ffd166",
        align: "center",
        padding: { x: 10, y: 6 },
        wordWrap: { width: Math.min(360, this.scale.width - 40) },
      })
      .setOrigin(0.5);
    this.mainContainer.add(banner);
    y += banner.height + 16;

    const note = this.add
      .text(centerX, y, "This won't spend a real attempt, save a result, or affect the leaderboard.", {
        fontFamily: "Arial, sans-serif",
        fontSize: "12px",
        color: "#8b93a7",
        align: "center",
        wordWrap: { width: Math.min(380, this.scale.width - 40) },
      })
      .setOrigin(0.5, 0);
    this.mainContainer.add(note);
    y = note.y + note.height + 22;

    const playButton = createButton(this, centerX, y, "PLAY QA PREVIEW", () => this.startQaPreview(), {
      width: 260,
      height: 56,
      fillColor: GameConfig.colors.star,
      hoverColor: 0xffd166,
      textColor: "#0f1117",
    });
    this.mainContainer.add(playButton);
    y += 72;

    return y;
  }

  // ---------------------------------------------------------------------
  // QA-only panel - only ever built on an allowed host (`isDailyQaAllowedHost`
  // already gated this call), and every action inside it is itself gated
  // again by the same allowlist inside `src/daily/dailyQa.ts`.
  // ---------------------------------------------------------------------

  private buildQaPanel(centerX: number, y: number): void {
    const toggle = createButton(this, centerX, y, this.qaPanelOpen ? "QA ▲" : "QA ▼", () => {
      this.qaPanelOpen = !this.qaPanelOpen;
      this.buildLayout();
    }, { width: 100, height: 32, fontSize: "12px", fillColor: 0x1c2030, hoverColor: 0x262c40, textColor: "#ffd166", silent: true });
    this.mainContainer.add(toggle);
    if (!this.qaPanelOpen) return;

    const hostname = window.location.hostname;
    const panelY = y - 220;

    if (!this.qaDateInput) {
      this.qaDateInput = createNativeTextInput({ maxLength: 10, placeholder: "YYYY-MM-DD" });
      this.qaDateInput.setValue(this.challengeId);
    }
    this.qaDateInput.layout(this, centerX - 90, panelY, 180, 34);
    this.qaDateInput.setVisible(true);

    const loadDate = createButton(this, centerX + 130, panelY + 17, "LOAD", () => {
      setDailyQaDate(hostname, this.qaDateInput?.getValue() ?? null);
      void this.refresh();
    }, { width: 90, height: 34, fontSize: "12px", silent: true });
    this.mainContainer.add(loadDate);

    // PREVIOUS DAY / NEXT DAY - jump the override one day either way
    // without retyping, for rapid visual review across consecutive
    // dates. Always relative to the currently-loaded challengeId (falls
    // back to today's real date if nothing is loaded yet), and always
    // goes through the same setDailyQaDate()+refresh() path LOAD uses -
    // switching this way replaces challengeId/spec/seed/layout/recipe
    // exactly as completely as typing a date and clicking LOAD does,
    // with no stale reuse from the previous date.
    const jumpDay = (deltaDays: number) => {
      const base = this.challengeId || this.spec?.challengeId;
      if (!base) return;
      const next = addDaysToChallengeId(base, deltaDays);
      this.qaDateInput?.setValue(next);
      setDailyQaDate(hostname, next);
      void this.refresh();
    };
    const prevDay = createButton(this, centerX - 148, panelY + 17, "◀ DAY", () => jumpDay(-1), { width: 80, height: 34, fontSize: "11px", fillColor: 0x1c2030, hoverColor: 0x262c40, textColor: "#f5f7fa", silent: true });
    const nextDay = createButton(this, centerX + 228, panelY + 17, "DAY ▶", () => jumpDay(1), { width: 80, height: 34, fontSize: "11px", fillColor: 0x1c2030, hoverColor: 0x262c40, textColor: "#f5f7fa", silent: true });
    this.mainContainer.add([prevDay, nextDay]);

    const clearDate = createButton(this, centerX, panelY + 50, "CLEAR DATE OVERRIDE", () => {
      setDailyQaDate(hostname, null);
      void this.refresh();
    }, { width: 220, height: 30, fontSize: "11px", fillColor: 0x1c2030, hoverColor: 0x262c40, textColor: "#f5f7fa", silent: true });
    this.mainContainer.add(clearDate);

    const simulated = getSimulatedAttemptsRemaining(hostname);
    const simText = this.add
      .text(centerX, panelY + 84, `Simulated attempts remaining: ${simulated ?? "off"}`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "11px",
        color: "#8b93a7",
      })
      .setOrigin(0.5);
    this.mainContainer.add(simText);

    const dec = createButton(this, centerX - 60, panelY + 110, "-1", () => {
      const current = simulated ?? this.attemptsRemaining;
      setSimulatedAttemptsRemaining(hostname, Math.max(0, current - 1));
      void this.refresh();
    }, { width: 60, height: 28, fontSize: "12px", silent: true });
    const clearSim = createButton(this, centerX + 20, panelY + 110, "RESET", () => {
      setSimulatedAttemptsRemaining(hostname, null);
      void this.refresh();
    }, { width: 90, height: 28, fontSize: "11px", fillColor: 0x1c2030, hoverColor: 0x262c40, textColor: "#f5f7fa", silent: true });
    this.mainContainer.add([dec, clearSim]);

    const resetAll = createButton(this, centerX, panelY + 150, "RESET ALL LOCAL DAILY STATE", () => {
      resetDailyQaState(hostname);
      DailyLocalState.clear();
      void this.refresh();
    }, { width: 260, height: 30, fontSize: "11px", fillColor: 0x1c2030, hoverColor: 0x262c40, textColor: "#ff5f6d", silent: true });
    this.mainContainer.add(resetAll);

    if (this.spec) {
      let specSummary: string;
      if (this.spec.formatVersion === 2) {
        specSummary = [
          `layout=${this.spec.layoutArchetype} variant=${this.spec.layoutVariant} theme=${this.spec.themeWorldId}`,
          `recipe=${this.spec.recipeId} bite=${this.spec.biteMode} modifiers=${this.spec.modifiers.join(",") || "none"}`,
          `start=${this.spec.startingLength} step=${this.spec.baseStepMs}ms scoring=${this.spec.scoringProfile.id}`,
          `genVersion=${this.spec.generatorVersion} seed=${this.spec.seed} formatV=2`,
        ].join("\n");
      } else if (this.spec.formatVersion === 3) {
        specSummary = [
          `tier=${this.spec.tier} intensity=${this.spec.intensity.toFixed(2)} archetype=${this.spec.archetype} theme=${this.spec.themeWorldId}`,
          `speed=${this.spec.speedProfileId} pressure=${this.spec.pressureProfileId} pacing=${this.spec.pacingId}`,
          `start=${this.spec.startingLength} step=${this.spec.baseStepMs}ms scoring=${this.spec.scoringProfile.id}`,
          `portals=${this.spec.portals.length} blackHoles=${this.spec.blackHoles.length} drivers=${this.spec.v6.majorDrivers.join(",") || "none"}`,
          `cycle=${this.spec.v6.cycleNumber} dayInCycle=${this.spec.v6.dayInCycle} genVersion=${this.spec.generatorVersion} seed=${this.spec.seed} formatV=3`,
        ].join("\n");
      } else {
        specSummary = [
          `family=${this.spec.mapFamily} theme=${this.spec.themeWorldId}`,
          `bite=${this.spec.biteMode} modifiers=${this.spec.modifiers.join(",") || "none"}`,
          `start=${this.spec.startingLength} target=${this.spec.targetLength} step=${this.spec.baseStepMs}ms`,
          `genVersion=${this.spec.generatorVersion} seed=${this.spec.seed} formatV=1 (LEGACY)`,
        ].join("\n");
      }
      const specText = this.add
        .text(centerX, panelY + 180, specSummary, {
          fontFamily: "monospace",
          fontSize: "10px",
          color: "#8b93a7",
          align: "center",
        })
        .setOrigin(0.5, 0);
      this.mainContainer.add(specText);
    }

    this.buildScoreChallengeQaPreview(centerX, panelY + 250);
  }

  /**
   * "Beat My Score" (PR10) QA preview: jumps straight into
   * `ScoreChallengeScene` with a synthetic fixture (see
   * `scoreChallengeQa.ts`) for every landing-card state the PR spec asks
   * for - never a real token, never a real network call, never a real
   * PostHog conversion event, never a consumed Daily attempt. Lives inside
   * this already-host-gated QA panel rather than a separate surface, same
   * "reuse the existing QA architecture" rule every other QA affordance in
   * this codebase follows.
   */
  private buildScoreChallengeQaPreview(centerX: number, y: number): void {
    const label = this.add
      .text(centerX, y, "SCORE CHALLENGE PREVIEW (QA)", { fontFamily: "Arial, sans-serif", fontSize: "11px", fontStyle: "bold", color: "#ffd166" })
      .setOrigin(0.5);
    this.mainContainer.add(label);
    y += 22;

    const columns = 4;
    const buttonWidth = 96;
    const buttonHeight = 26;
    const gap = 6;
    const rowWidth = columns * buttonWidth + (columns - 1) * gap;
    const startX = centerX - rowWidth / 2 + buttonWidth / 2;

    const buttons = SCORE_CHALLENGE_QA_PRESETS.map((preset: ScoreChallengeQaPresetId, index: number) => {
      const col = index % columns;
      const row = Math.floor(index / columns);
      const x = startX + col * (buttonWidth + gap);
      const rowY = y + row * (buttonHeight + gap);
      return createButton(this, x, rowY, preset.toUpperCase().replace(/_/g, " "), () => this.scene.start("ScoreChallengeScene", { qaPreset: preset }), {
        width: buttonWidth,
        height: buttonHeight,
        fontSize: "9px",
        fillColor: 0x1c2030,
        hoverColor: 0x262c40,
        textColor: "#f5f7fa",
      });
    });
    this.mainContainer.add(buttons);
  }
}

const MODIFIER_LABEL: Record<string, string> = {
  long_snake: "LONG SNAKE",
  sprint_target: "SHORT SPRINT",
  high_speed: "HIGH SPEED",
  tight_navigation: "TIGHT NAVIGATION",
};

function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}
